import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import get from 'lodash.get';
import classNames from 'classnames';

import { loadMenu } from 'redux/modules/menu';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { HAS_CUSTOM_TITLE } from 'lib/brandFeatures';
import { parseIdFromUrl } from 'lib/parseIdFromUrl';
import { appendSearchParam } from 'lib/link';

import Link from 'components/Link';
import { Button } from 'components/Button';
import './styles.themed.scss';

/**
 *
 * @param {object} props
 * @param {string} props.article
 * @param {object} props.menu
 * @param {object} props.shared
 */
const mapStateToProps = ({ article, menu, shared }) => ({
  articleId: article.content?.[0]?.id,
  menu: menu.current,
  vertical: shared.vertical,
});

/**
 *
 * @param {Function} dispatch
 */
const mapActionsToProps = (dispatch) => ({
  /**
   *
   * @param {string} id
   */
  loadEmbedMenu: (id) => dispatch(loadMenu(id)),
});

/**
 * Extracts the menuId from an HTML string and formats it based on vertical.
 * @param {string} htmlString - The HTML string containing embedId.
 * @param {string} vertical - The vertical/section name (e.g., 'today').
 * @returns {string|null} The formatted menuId or null if not found.
 */
export function getMenuId(htmlString, vertical) {
  const match = htmlString?.match(/embedId=["“”]([0-9]+)["“”]/);
  const menuId = match?.[1];
  if (!menuId) return null;
  return vertical === 'today' ? `tdmn${menuId}` : `ncmn${menuId}`;
}

/**
 * Renders a menu embed widget.
 *
 * @param {object} props
 * @param {string} [props.articleId] - The article ID for context.
 * @param {string} [props.html] - The HTML string containing the embedId.
 * @param {object} [props.menu] - Menu data, may include name, id, and menuItems.
 * @param {Function} [props.loadEmbedMenu] - Function to load menu data by ID.
 * @param {string} [props.className] - Optional CSS class for the root element.
 * @param {string} props.vertical - The vertical/section name (e.g., 'today').
 * @param {Function} [props.getDataActivityMapID] - Optional function for activity map ID.
 */
export function MenuEmbed(props) {
  const {
    articleId,
    html,
    menu,
    loadEmbedMenu,
    className,
    vertical,
    getDataActivityMapID,
  } = props;

  useEffect(() => {
    const menuId = getMenuId(html, vertical);
    if (menuId) {
      loadEmbedMenu(menuId);
    }
  }, [html, loadEmbedMenu, getMenuId]);

  // Render "See More" button
  /**
   *
   * @param {string} url
   * @param {number} i
   */
  const renderSeeMoreButton = (url, i) => (
    <Button
      key={i + url}
      size="normal"
      type="link"
      url={url}
      title="See More"
    />
  );

  const renderMenuItems = useCallback(
    (menuItems) => menuItems.map((item, i) => {
      const url = get(item, 'url.primary', null);
      const linkId = parseIdFromUrl(url);
      const { text = '' } = item;

      if (articleId === linkId) return null;
      if (i > 6) return null;
      if (i === 6) {
        return renderSeeMoreButton(url, i);
      }

      const icid = `relatedList_${i + 1}`;
      return (
        <li className="menu-embed__link" key={`${i + url}`}>
          <Link href={appendSearchParam(url, { icid })} icid={icid}>
            {text.slice(0, 100)}
          </Link>
        </li>
      );
    }),
    [articleId],
  );

  const { name = '', menuItems = [] } = menu || {};
  const hasCustomTitle = getFeatureConfigForBrand(HAS_CUSTOM_TITLE, vertical);
  const shortenedTitle = name.slice(0, 30);

  const dataActivityMapID = getDataActivityMapID
    && getDataActivityMapID({
      componentName: 'menu-embed',
      pageRegion: 'article-body',
      componentTitle: shortenedTitle,
    });

  return menuItems.length ? (
    <div
      className={className}
      data-activity-map={dataActivityMapID}
      data-test="menu-embed"
      data-testid="menu-embed"
    >
      <h4
        className={classNames('menu-embed__title', {
          'menu-embed__title--default': !hasCustomTitle,
          'menu-embed__title--custom': hasCustomTitle,
        })}
      >
        {shortenedTitle}
      </h4>
      <ul className="menu-embed__items">{renderMenuItems(menuItems)}</ul>
    </div>
  ) : null;
}

export const ConnectedMenuEmbed = connect(mapStateToProps, mapActionsToProps)(MenuEmbed);
